// src/screens/admin/CotizadorPanel.jsx
// Pestaña "Cotizador" del Panel de Administrador (solo Yoedis). Reemplaza el
// Excel Calculadora-final-v2: lista de cotizaciones guardadas, cotización
// con una fila por día de vuelo, ajustes, horarios del ómnibus y conversor.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from '../../components/UI';
import {
  getAjustes, listarHorarios, listarCotizaciones, crearCotizacion, duplicarCotizacion, borrarCotizacion,
} from '../../services/cotizadorService';
import { calcularOpcion, mejorOpcion, fmtBRL, fmtFecha, usaMargen } from '../../services/cotizadorCalc';
import CotizadorCotizacion from './CotizadorCotizacion';
import { CotizadorAjustes, CotizadorHorarios, CotizadorConversor } from './CotizadorAjustes';
import { inputStyle, cardStyle, btnChico, ESTADOS } from './CotizadorUI';

const SECCIONES = [
  { id: 'lista', label: 'Cotizaciones' },
  { id: 'horarios', label: 'Horarios' },
  { id: 'ajustes', label: 'Ajustes' },
  { id: 'dolares', label: 'Dólares' },
];

export default function CotizadorPanel() {
  const [seccion, setSeccion] = useState('lista');
  const [abierta, setAbierta] = useState(null);
  const [ajustes, setAjustes] = useState(null);
  const [horarios, setHorarios] = useState(null);
  const [cotizaciones, setCotizaciones] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [creando, setCreando] = useState(false);

  const recargarHorarios = useCallback(() => listarHorarios().then(setHorarios), []);
  const recargarLista = useCallback(() => (
    listarCotizaciones().then(setCotizaciones).catch(() => toast('No se pudieron cargar las cotizaciones.', 'error'))
  ), []);

  useEffect(() => {
    getAjustes().then(setAjustes);
    recargarHorarios();
  }, [recargarHorarios]);

  useEffect(() => { if (!abierta && seccion === 'lista') recargarLista(); }, [abierta, seccion, recargarLista]);

  const filas = useMemo(() => {
    if (!cotizaciones || !ajustes || !horarios) return [];
    const q = busqueda.trim().toLowerCase();
    return cotizaciones
      .filter((c) => !q || (c.nombre || '').toLowerCase().includes(q))
      .map((c) => {
        const res = c.opciones.map((op) => calcularOpcion({ cotizacion: c, opcion: op, ajustes, horarios }));
        const i = mejorOpcion(res);
        return { c, mejor: i >= 0 ? res[i] : null };
      });
  }, [cotizaciones, ajustes, horarios, busqueda]);

  if (!ajustes || !horarios) return <p style={{ color: 'var(--muted)', textAlign: 'center', fontSize: 13 }}>Cargando…</p>;

  const nueva = async (tipo) => {
    setCreando(true);
    try { setAbierta(await crearCotizacion(ajustes, tipo)); } catch { toast('No se pudo crear la cotización.', 'error'); }
    setCreando(false);
  };
  const duplicar = async (c) => {
    try { await duplicarCotizacion(c.id); toast('Duplicada ✅'); recargarLista(); } catch { toast('No se pudo duplicar.', 'error'); }
  };
  const borrar = async (c) => {
    if (!window.confirm(`¿Borrar "${c.nombre || 'sin nombre'}"? No se puede deshacer.`)) return;
    try { await borrarCotizacion(c.id); recargarLista(); } catch { toast('No se pudo borrar.', 'error'); }
  };

  if (abierta) {
    return <CotizadorCotizacion id={abierta} ajustes={ajustes} horarios={horarios} onBack={() => setAbierta(null)} />;
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto' }}>
        {SECCIONES.map((s) => (
          <button key={s.id} onClick={() => setSeccion(s.id)} style={{
            ...btnChico, flex: '1 0 auto',
            background: seccion === s.id ? 'var(--navy-card)' : 'transparent',
            color: seccion === s.id ? 'var(--orange)' : 'var(--muted)',
            borderColor: seccion === s.id ? 'var(--orange)' : 'var(--navy-border)',
          }}>{s.label}</button>
        ))}
      </div>

      {seccion === 'lista' && (
        <div>
          <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar por nombre…" style={{ ...inputStyle, marginBottom: 8 }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
            <button className="btn-primary" onClick={() => nueva('directo')} disabled={creando} style={{ width: 'auto', padding: '10px 6px', fontSize: 13 }}>
              {creando ? '…' : '+ Directo'}
            </button>
            <button onClick={() => nueva('revendedor')} disabled={creando} style={{ ...btnChico, color: 'var(--orange)', borderColor: 'var(--orange)', fontWeight: 700 }}>
              + Revendedor
            </button>
            <button onClick={() => nueva('particular')} disabled={creando} style={{ ...btnChico, color: 'var(--orange)', borderColor: 'var(--orange)', fontWeight: 700 }}>
              + Particular
            </button>
          </div>
          {cotizaciones === null && <p style={{ color: 'var(--muted)', textAlign: 'center', fontSize: 13 }}>Cargando…</p>}
          {cotizaciones && filas.length === 0 && (
            <p style={{ color: 'var(--muted)', textAlign: 'center', fontSize: 13 }}>
              {busqueda ? 'Ninguna cotización con ese nombre.' : 'Todavía no hay cotizaciones. Toca "+ Directo", "+ Revendedor" o "+ Particular".'}
            </p>
          )}
          {filas.map(({ c, mejor }) => {
            const est = mejor?.estado ? ESTADOS[mejor.estado] : null;
            return (
              <div key={c.id} style={{ ...cardStyle, borderLeftWidth: 4, borderLeftColor: est?.color || 'var(--navy-border)', padding: 12 }}>
                <div onClick={() => setAbierta(c.id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nombre || '(sin nombre)'}</p>
                    <p style={{ fontSize: 11, color: 'var(--muted)' }}>
                      {c.tipo === 'revendedor' ? `Revendedor${c.revendedor?.nombre ? ` · ${c.revendedor.nombre}` : ''}` : c.tipo === 'particular' ? 'Particular' : `Llegó ${fmtFecha(c.llegada)}`}
                    </p>
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 800, color: est?.color || 'var(--muted)', whiteSpace: 'nowrap' }}>
                    {mejor?.ganancia == null ? '—' : `R$ ${fmtBRL(mejor.ganancia, usaMargen(c.tipo) ? 2 : 0)}`}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button onClick={() => duplicar(c)} style={{ ...btnChico, flex: 1 }}>Duplicar</button>
                  <button onClick={() => borrar(c)} style={{ ...btnChico, flex: 1, color: 'var(--error)' }}>Borrar</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {seccion === 'horarios' && <CotizadorHorarios horarios={horarios} onRecargar={recargarHorarios} />}
      {seccion === 'ajustes' && <CotizadorAjustes ajustes={ajustes} onChange={setAjustes} />}
      {seccion === 'dolares' && <CotizadorConversor />}
    </div>
  );
}
